import { useCallback, useState } from 'react';
import { AlertTriangle, Ban, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

import { documentCaptureService } from '../../services/documentCaptureService';
import { useDocumentCaptureStore } from '../../store/documentCaptureStore';

/**
 * Bulk reject for the Scans tab (86d3x48q7) — the junk sweep.
 *
 * ## Reject only. Never approve.
 *
 * There is no bulk approve, accept-all or one-click sweep anywhere in this feature, and the
 * asymmetry is deliberate rather than an omission: rejecting a scan files nothing and links
 * nothing, while approving one commits a document against a customer's order. Bulk-approving
 * identifications is exactly the rubber-stamp risk the whole design is built to prevent — and the
 * measured error source here is people, not readers (staff hand-matching misfiled 2 of 45 links,
 * including a cross-customer double-file 36 seconds apart). A sweep button on that side would
 * industrialise it.
 *
 * ## Unattached scans only
 *
 * The Scans tab holds scans with no document. Rejecting an ATTACHED one clears a live NetSuite
 * custbody field — a `docscan.manage` act that stays deliberate and one at a time. The server
 * refuses attached items here too; this is not a UI-only rule.
 *
 * ## Per-item outcomes
 *
 * The server decides each item on its own and commits it on its own, so one item going stale
 * underneath the reviewer does not roll back the other forty-nine. Failures are named and stay OPEN
 * in the queue.
 */
interface BulkRejectBarProps {
  selectedIds: number[];
  onClear: () => void;
}

export function BulkRejectBar({ selectedIds, onClear }: BulkRejectBarProps) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const fetchReviewQueue = useDocumentCaptureStore((s) => s.fetchReviewQueue);

  const count = selectedIds.length;

  const confirm = useCallback(async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      const result = await documentCaptureService.bulkRejectScans(selectedIds, reason.trim());
      if (result.failed.length === 0) {
        toast.success(`${result.rejected} ${result.rejected === 1 ? 'scan' : 'scans'} rejected`);
      } else {
        // Name what did NOT go through. A bare "8 of 10" leaves the reviewer to work out which two
        // are still sitting in their queue and why.
        toast.warning(`${result.rejected} of ${result.requested} rejected`, {
          description: result.failed
            .slice(0, 3)
            .map((f) => `${f.label ?? `item ${f.reviewItemId}`}: ${f.message}`)
            .join('\n'),
        });
      }
      setAsking(false);
      setReason('');
      onClear();
      await fetchReviewQueue();
    } catch {
      toast.error('That didn’t go through. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [selectedIds, reason, onClear, fetchReviewQueue]);

  if (count === 0) return null;

  return (
    <div className="flex-shrink-0 border-b border-border">
      <div className="flex items-center gap-2 bg-accent px-3 py-2">
        <span className="text-xs font-semibold text-foreground">
          {count} {count === 1 ? 'item' : 'items'} selected
        </span>
        <span className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          className="h-7 border-destructive/40 text-destructive hover:bg-destructive/10"
          onClick={() => setAsking((v) => !v)}
          disabled={busy}
        >
          <Ban className="mr-1.5 h-3 w-3" aria-hidden />
          Reject {count}
        </Button>
        <Button variant="ghost" size="sm" className="h-7" onClick={onClear} disabled={busy}>
          Clear
        </Button>
      </div>

      {asking && (
        <div className="flex flex-col gap-[9px] border-t border-border bg-destructive/5 px-[14px] py-3">
          <div className="flex gap-2">
            {/* The consequence sentence carries a destructive glyph, per the prototype. Unmarked it
                read as an ordinary hint above a text box rather than the last thing between the
                reviewer and rejecting ten scans at once. */}
            <AlertTriangle
              className="mt-px h-[15px] w-[15px] flex-shrink-0 text-destructive"
              aria-hidden
            />
            <p className="text-xs leading-[1.55] text-foreground">
              {count === 1
                ? 'Reject this item? The reason is recorded against it — it stays in the archive for audit, reopenable by a manager.'
                : `Reject ${count} items? One reason is recorded against each — they stay in the archive for audit, reopenable by a manager.`}
            </p>
          </div>
          <label htmlFor="bulk-reject-reason" className="sr-only">
            Why are these being rejected?
          </label>
          {/* `bg-background` is load-bearing, not decoration. The shared `Textarea` is
              `bg-transparent` — fine on a plain card, but this one sits on the destructive-tinted
              confirm panel, where it soaked up the pink and stopped reading as a field you type in.
              The prototype sets an explicit background here for the same reason. */}
          <Textarea
            id="bulk-reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. Duplicate batch — the good copies were re-scanned this morning."
            className="min-h-0 resize-y rounded-lg bg-background px-2.5 py-2 text-xs leading-normal md:text-xs"
          />
          <div className="flex gap-2">
            <Button variant="destructive" size="sm" disabled={!reason.trim() || busy} onClick={() => void confirm()}>
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />}
              Reject {count} {count === 1 ? 'item' : 'items'}
            </Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setAsking(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
